import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useCollectionBinding, useSlotGroup, EditableCollectionBinding, EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { clean, hoursRows, stagger, telHref, type OfficeHour } from '../utils/quinn';
import { useRawProperty } from '../utils/hooks';
import { PATHS, useSiteFlags } from '../utils/site';

const contactCtaDef: SlotGroupDefinition = {
  label: 'Contact Button',
  fields: {
    label: { label: 'Button Text', type: 'text', defaultValue: 'Contact Us' },
    url: { label: 'URL', type: 'url', defaultValue: '/contact-us' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

/** FAQ page close: the leasing team's contact and office hours for questions the list didn't answer. */
export function FaqHelp() {
  const p = useRawProperty();
  const flags = useSiteFlags();
  const contact = useSlotGroup('faqhelp.contact_cta', contactCtaDef);
  const hours = useCollectionBinding<OfficeHour>('faqhelp.hours', { collection: 'office_hours' });
  const tel = telHref(flags.phone);
  const email = clean(p.property_email);
  const rows = hoursRows(hours, false);

  return (
    <section
      className="flex items-end justify-between gap-24 bg-[--q-surface] px-[--gutter] py-28 max-[1199px]:flex-col max-[1199px]:items-stretch max-[1199px]:gap-12 max-[767px]:gap-6 max-[767px]:py-14"
      id="faq-help"
    >
      <div className="flex w-[720px] min-w-0 flex-col gap-6 max-[1199px]:w-auto max-[767px]:gap-4" data-reveal>
        <RichTextContent path="faqhelp.eyebrow" as="div" className="eyebrow" />
        <RichTextContent path="faqhelp.title" as="h2" className="h1 [&_p]:m-0" />
        <RichTextContent
          path="faqhelp.lead"
          as="p"
          className="m-0 max-w-[600px] text-lg leading-7 text-[--q-muted] max-[767px]:text-[15px] max-[767px]:leading-[23px] [&_p]:m-0"
        />
        <div className="flex gap-3 pt-2 max-[767px]:flex-col max-[767px]:gap-2.5">
          <EditableSlotGroup groupPath="faqhelp.contact_cta" definition={contactCtaDef}>
            <a className="btn btn-primary [&_p]:m-0" href={contact.url || PATHS.contact} target={contact._target || '_self'}>
              {contact.label || 'Contact Us'}
            </a>
          </EditableSlotGroup>
          {tel && (
            <a className="btn btn-secondary dniphonehref [&_p]:m-0" href={tel}>
              <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" className="shrink-0 fill-none stroke-current stroke-[1.7] [stroke-linejoin:round]">
                <path d="M5 4h4l2 5-2.5 1.5a11 11 0 005 5L15 13l5 2v4a2 2 0 01-2 2A16 16 0 013 6a2 2 0 012-2" />
              </svg>
              <RichTextContent path="faqhelp.call_label" as="span" />
            </a>
          )}
        </div>
      </div>
      {(tel || email || rows.length > 0) && (
        <div className="faqhelp__office flex w-[360px] shrink-0 flex-col border-t border-t-[--q-border] max-[1199px]:w-auto" data-reveal style={stagger(1)}>
          <RichTextContent path="faqhelp.office_label" as="div" className="label pb-2.5 pt-4 [&_p]:m-0" />
          {(tel || email) && (
            <div className="flex flex-col gap-0.5 border-t border-t-[--q-border] py-4">
              {tel && (
                <a
                  className="dniphone dniphonehref block items-center justify-between gap-4 font-heading text-[22px] leading-7 text-[--q-ink] no-underline [border:0] hover:underline hover:underline-offset-[3px]"
                  href={tel}
                >
                  {flags.phone}
                </a>
              )}
              {email && (
                <a
                  className="block items-center justify-between gap-4 text-sm leading-5 text-[--q-muted] no-underline [border:0] [overflow-wrap:anywhere] hover:underline hover:underline-offset-[3px]"
                  href={`mailto:${email}`}
                >
                  {email}
                </a>
              )}
            </div>
          )}
          <EditableCollectionBinding
            bindingPath="faqhelp.hours"
            label="Office Hours"
            className="flex flex-col border-y border-y-[--q-border] py-2 empty:hidden"
          >
            {rows.map(([d, t]) => (
              <div className="flex justify-between gap-4 py-1.5 text-[15px] leading-[22px]" key={d}>
                <span>{d}</span>
                <span className="text-[--q-muted]">{t}</span>
              </div>
            ))}
          </EditableCollectionBinding>
        </div>
      )}
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FaqHelp',
  displayName: 'FAQ Help',
  tokens: {
    'faqhelp.eyebrow': { label: 'Eyebrow', type: 'richtext', defaultValue: '<p>Still need help?</p>' },
    'faqhelp.title': { label: 'Title', type: 'richtext', defaultValue: '<p>Couldn’t find your answer?<br>We’re happy to help.</p>' },
    'faqhelp.lead': {
      label: 'Lead',
      type: 'richtext',
      defaultValue: '<p>Our leasing team can walk you through fees, qualifications and move-in, or set up a time to see a home.</p>',
    },
    'faqhelp.call_label': { label: 'Call Button Text', type: 'richtext', defaultValue: '<p>Call the Leasing Team</p>' },
    'faqhelp.office_label': { label: 'Office Label', type: 'richtext', defaultValue: '<p>Leasing office</p>' },
  },
  slotGroups: {
    'faqhelp.contact_cta': contactCtaDef,
  },
  collectionBindings: {
    'faqhelp.hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(FaqHelp as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default FaqHelp;
